import React, { useEffect, useState } from 'react';
import { Form, Input, Select } from 'antd';
import { ErrorMessage } from 'formik';
import { Button } from 'shared/ui/Button/Button';
import classNames from 'classnames';
import { PencilIcon } from '@heroicons/react/24/outline';
import { Switch } from '@headlessui/react';
import { countries, filterOption } from 'shared/ui/form/CountrySelect';
import { findAlpha2ByName } from 'shared/lib/countries';

export const SelectClientFormPart: React.FC<{
  values: Record<string, any>;
  clients: Record<string, any>[];
  validateForm: (values: Record<string, any>) => void;
  toggleIncludeClientDetails: () => void;
  setFieldValue: (field: string, val: any) => void;
  isEditMode: boolean;
  includeClientDetails: boolean;
}> = ({
  values,
  clients,
  setFieldValue,
  validateForm,
  includeClientDetails,
  toggleIncludeClientDetails,
  isEditMode,
}) => {
  const [updateClientMode, setUpdateClientMode] = useState(false);
  useEffect(() => {
    validateForm(values);
  }, [values.clientName, values.clientEmail]);

  return (
    <>
      {!updateClientMode && (
        <Form.Item layout="vertical" label="Your Client">
          <Select
            size="large"
            showSearch
            placeholder={'Select or Create New'}
            filterOption={filterOption}
            onChange={(val: any) => {
              setFieldValue('clientId', val);

              if (val === 'new') {
                setFieldValue('clientName', '');
                setFieldValue('clientEmail', '');
                setFieldValue('clientStreetAddress', '');
                setFieldValue('clientCity', '');
                setFieldValue('clientCountry', undefined);
                setFieldValue('clientPostcode', '');
                setFieldValue('clientVatNumber', '');
              } else {
                const client = clients.find((p) => p.id === val);
                setFieldValue('clientName', client.name);
                setFieldValue(
                  'clientEmail',
                  client.billingEmail || client.email
                );
                setFieldValue(
                  'clientStreetAddress',
                  client.address?.streetAddress
                );
                setFieldValue('clientCity', client.address?.city);
                setFieldValue('clientCountry', client.address?.country);
                setFieldValue('clientPostcode', client.address?.postalCode);
                setFieldValue('clientVatNumber', client.address?.vatNumber);
              }
            }}
            value={values.clientId}
            options={[
              {
                value: 'new',
                label: '+ Add New Client',
              },
              ...clients.map((client) => ({
                value: client.id,
                label: (
                  <>
                    {client.name} - {client.billingEmail || client.email}
                  </>
                ),
              })),
            ]}
          />
        </Form.Item>
      )}

      {values.clientId &&
        values.clientId !== 'new' &&
        values.clientId !== undefined && (
          <div className="col-span-8 sm:col-span-6">
            <div
              id="alert-5"
              className="rounded-lg bg-gray-50 p-4  text-left dark:bg-slate-700"
              role="alert">
              <div className="text-sm text-gray-800 dark:text-slate-400">
                {!!values.clientName && (
                  <>
                    <span>Name: </span>
                    <span className="dark:text-slate-300">
                      {values.clientName}
                    </span>
                  </>
                )}

                {!!values.clientEmail && (
                  <>
                    <br /> <span>Email: </span>
                    <span className="dark:text-slate-300">
                      {values.clientEmail}
                    </span>
                  </>
                )}

                {!!values.clientStreetAddress && (
                  <>
                    <br /> <span>Address: </span>
                    <span className="dark:text-slate-300">
                      {values.clientStreetAddress}
                      {!!values.clientCity && <>, {values.clientCity}</>}
                      {!!values.clientCountry && <>, {values.clientCountry}</>}
                      {!!values.clientPostcode && (
                        <>, {values.clientPostcode}</>
                      )}
                    </span>
                  </>
                )}

                {!!values.clientVatNumber && (
                  <>
                    <br /> <span>VAT / EIN: </span>
                    <span className="dark:text-slate-300">
                      {values.clientVatNumber}
                    </span>
                  </>
                )}

                {isEditMode && (
                  <Button
                    onClick={() => {
                      setUpdateClientMode(true);
                      setFieldValue('clientId', 'new');
                    }}
                    size="xs"
                    color="gray"
                    className="mt-3">
                    <PencilIcon width={12} className="mr-2" />
                    Edit Client
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

      {values.clientId === 'new' && (
        <>
          <div className="grid grid-cols-6 gap-6">
            <Form.Item
              className="col-span-6 sm:col-span-3"
              label="Client Name"
              layout="vertical"
              help="Name of your client.">
              <Input
                placeholder="e.g. Acme Corporation"
                onChange={(evt) => {
                  setFieldValue('clientName', evt.target.value);
                }}
                size="large"
                value={values.clientName}
              />
            </Form.Item>

            <Form.Item
              className="col-span-6 sm:col-span-3"
              label="Client Email"
              layout="vertical"
              help="Client email which will be displayed on the invoice.">
              <Input
                placeholder="e.g. client@acme.com"
                onChange={(evt) => {
                  setFieldValue('clientEmail', evt.target.value);
                }}
                size="large"
                value={values.clientEmail}
              />
            </Form.Item>
          </div>

          <div className="grid grid-cols-6 gap-6">
            <div className="col-span-6 sm:col-span-3">
              <div className="col-span-6 sm:col-span-3">
                <Switch.Group as="div" className="items-left flex ">
                  <Switch.Label
                    as="span"
                    className="mr-5 text-sm font-medium leading-6 text-secondary-200"
                    passive>
                    Include Client Address
                  </Switch.Label>
                  <Switch
                    checked={includeClientDetails}
                    onChange={toggleIncludeClientDetails}
                    className={classNames(
                      includeClientDetails ? 'bg-indigo-600' : 'bg-gray-500',
                      'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2'
                    )}>
                    <span
                      aria-hidden="true"
                      className={classNames(
                        includeClientDetails
                          ? 'translate-x-5'
                          : 'translate-x-0',
                        'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                      )}
                    />
                  </Switch>
                </Switch.Group>
              </div>
            </div>
          </div>

          {includeClientDetails && (
            <>
              <div className="grid grid-cols-6 gap-6">
                <Form.Item
                  className="col-span-6 sm:col-span-3"
                  label="Client House & Street Name"
                  layout="vertical"
                  help="First line of address.">
                  <Input
                    placeholder="e.g. 123 Main Street"
                    onChange={(evt) => {
                      setFieldValue('clientStreetAddress', evt.target.value);
                    }}
                    size="large"
                    value={values.clientStreetAddress}
                  />
                </Form.Item>

                <Form.Item
                  className="col-span-6 sm:col-span-3"
                  label="Client City"
                  layout="vertical"
                  help="City where business is registered.">
                  <Input
                    placeholder="e.g. London"
                    onChange={(evt) => {
                      setFieldValue('clientCity', evt.target.value);
                    }}
                    size="large"
                    value={values.clientCity}
                  />
                </Form.Item>
              </div>

              <div className="grid grid-cols-6 gap-6">
                <Form.Item
                  className="col-span-6 sm:col-span-3"
                  layout="vertical"
                  help={'Country of business.'}
                  label="Client Country">
                  <Select
                    size="large"
                    showSearch
                    placeholder={'Select country'}
                    filterOption={filterOption}
                    onChange={(val) => {
                      const country = countries.find(
                        (country) => country.value === val
                      );
                      setFieldValue('clientCountry', country.label);
                    }}
                    value={findAlpha2ByName(values.clientCountry)}
                    options={countries.map(({ label, value, flag }) => ({
                      value,
                      label: (
                        <>
                          {flag} {label}
                        </>
                      ),
                    }))}
                  />
                </Form.Item>

                <Form.Item
                  className="col-span-6 sm:col-span-3"
                  label="Client ZIP Code"
                  layout="vertical"
                  help="Business ZIP / post code.">
                  <Input
                    placeholder="e.g. 12345"
                    onChange={(evt) => {
                      setFieldValue('clientPostcode', evt.target.value);
                    }}
                    size="large"
                    value={values.clientPostcode}
                  />
                </Form.Item>
              </div>

              <div className="grid grid-cols-6 gap-6">
                <Form.Item
                  className="col-span-6 sm:col-span-3"
                  label="Client VAT / EIN Number"
                  layout="vertical"
                  help="Please input the VAT / EIN number. If you don't have one, leave it blank.">
                  <Input
                    placeholder="e.g. GB123456789"
                    onChange={(evt) => {
                      setFieldValue('clientVatNumber', evt.target.value);
                    }}
                    size="large"
                    value={values.clientVatNumber}
                  />
                </Form.Item>
              </div>
            </>
          )}
        </>
      )}

      <div>
        <ErrorMessage
          name="clientName"
          component="div"
          className="formik-error-message mt-2 text-xs text-rose-500"
        />

        <ErrorMessage
          name="clientEmail"
          component="div"
          className="formik-error-message mt-2 text-xs text-rose-500"
        />
      </div>
    </>
  );
};
